import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import DebugStatusPanel from './DebugStatusPanel';

const HeaderNav = ({ isPrivate = false }) => {
  const { 
    isAuthenticated, 
    ensName, 
    address, 
    login, 
    logout, 
    authStatus,
    setShowWalletModal,
    hasZwalletCreated,
    isZwalletConnected,
    showZwalletForCreation,
    connectZpostWithENS,
    integrationStatus = 'ready'
  } = useContext(AuthContext);
  
  const navigate = useNavigate();
  
  const displayName = ensName || (address ? `${address.substring(0, 6)}...` : '');

  // Format wallet for display
  const formatWalletAddress = (addr) => {
    if (!addr) return 'None';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  // For Landing Page (Public) Header - ENS GATED
  if (!isPrivate) {
    return (
      <>
        {/* Debug Status Panel - Always visible */}
        <DebugStatusPanel />
        
        <header className="nav">
          <div className="container nav-content">
            <div className="logo">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <span className="gold-logo" style={{ fontSize: '1.8em', fontWeight: 'bold' }}>
                  Zpost
                </span>
              </Link>
              <span className="subtitle-btn-color" style={{ fontSize: '0.7em' }}>
                The future of Free Speech. Own your .zpost.eth identity.
              </span>
            </div>
            <div className="nav-links">
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {/* ENS-GATED ACCESS - Only for .zpost.eth owners */}
                <button 
                  className="btn btn-dark-red equal-btn" 
                  onClick={() => {
                    console.log('🚪 ENS-Gated Access clicked');
                    
                    if (isAuthenticated && ensName && ensName.endsWith('.zpost.eth')) {
                      console.log('✅ Already authenticated with .zpost.eth, going to app...');
                      navigate('/home');
                      return;
                    }
                    
                    if (address && (!ensName || !ensName.endsWith('.zpost.eth'))) {
                      console.log('⚠️ Wallet connected but missing .zpost.eth ENS');
                      alert('You need a .zpost.eth ENS domain to access Zpost.\n\nPlease mint your .zpost.eth domain first.');
                      navigate('/mint');
                      return;
                    }
                    
                    if (!hasZwalletCreated) {
                      console.log('🆕 No wallet found, creating wallet first...');
                      showZwalletForCreation();
                      return;
                    }
                    
                    if (!isZwalletConnected) {
                      console.log('🔓 Unlocking wallet for ENS verification...');
                      login({ mode: 'unlock' });
                      return;
                    }
                    
                    console.log('🔍 Checking for .zpost.eth ENS domain...');
                    connectZpostWithENS();
                  }}
                  disabled={authStatus === 'connecting' || authStatus === 'checking-ens' || authStatus === 'creating-wallet'}
                  title="Requires .zpost.eth ENS domain"
                >
                  {isAuthenticated && ensName?.endsWith('.zpost.eth') ? 'Go to App' : 
                   authStatus === 'checking-ens' ? 'Verifying ENS...' : 
                   authStatus === 'connecting' ? 'Connecting...' : 
                   authStatus === 'creating-wallet' ? 'Creating...' : 'Connect Zpost'}
                </button>
                
                {/* CREATE/MANAGE ENS DOMAIN */}
                <button 
                  className="btn btn-gold equal-btn"
                  onClick={() => {
                    console.log('🏷️ ENS Domain Management');
                    if (!hasZwalletCreated) {
                      showZwalletForCreation();
                    } else if (!isZwalletConnected) {
                      login({ mode: 'unlock', skipENSCheck: true });
                    } else {
                      navigate('/mint');
                    }
                  }}
                  disabled={authStatus === 'creating-wallet' || authStatus === 'unlocking'}
                >
                  {authStatus === 'creating-wallet' ? 'Creating...' : 
                   authStatus === 'unlocking' ? 'Unlocking...' : 'Get .zpost.eth'}
                </button>
                
                {/* WALLET MANAGEMENT */}
                <button 
                  className="btn btn-dark-red equal-btn"
                  onClick={() => {
                    console.log('🏦 Wallet Management');
                    if (!hasZwalletCreated) {
                      showZwalletForCreation();
                    } else if (!isZwalletConnected) {
                      login({ mode: 'unlock', skipENSCheck: true });
                    } else {
                      setShowWalletModal(true);
                    }
                  }}
                  disabled={authStatus === 'creating-wallet' || authStatus === 'unlocking'}
                >
                  {authStatus === 'creating-wallet' ? 'Creating...' : 
                   authStatus === 'unlocking' ? 'Unlocking...' : 'Zwallet'}
                </button>
              </div>
            </div>
          </div>
        </header>
      </>
    );
  }

  // For Private App Header (only shown after ENS-gated login)
  return (
    <>
      {/* Debug Status Panel */}
      <DebugStatusPanel />
      
      <header className="nav">
        <div className="container nav-content">
          <div className="logo">
            <Link to="/home" style={{ textDecoration: 'none' }}>
              <span className="gold-logo" style={{ fontSize: '1.8em', fontWeight: 'bold' }}>
                Zpost
              </span>
            </Link>
            <span className="subtitle-btn-color" style={{ fontSize: '0.7em' }}>
              The future of Free Speech. Own your .zpost.eth identity.
            </span>
          </div>
          <div className="nav-links">
            {/* ENS-GATED USER STATUS */}
            {isAuthenticated && ensName && ensName.endsWith('.zpost.eth') ? (
              <>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '1rem',
                  background: 'rgba(255, 215, 0, 0.1)',
                  padding: '0.5rem 1rem',
                  borderRadius: '9999px',
                  border: '1px solid var(--gold)'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--dark-red)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--gold)',
                    fontWeight: 'bold',
                    marginRight: '0.5rem',
                    fontSize: '0.8rem'
                  }}>
                    {ensName.charAt(0).toUpperCase()}
                  </div>
                  <span style={{ 
                    color: 'var(--gold)', 
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}>
                    {ensName}
                  </span>
                </div>
                
                <button className="btn btn-dark-red equal-btn" onClick={logout}>
                  Logout
                </button>
              </>
            ) : (
              <button 
                className="btn btn-dark-red equal-btn" 
                onClick={() => {
                  alert('You need a .zpost.eth ENS domain to access Zpost.\n\nPlease mint your .zpost.eth domain first.');
                  navigate('/mint');
                }}
                style={{ background: '#800000', color: '#fff' }}
              >
                ⚠️ Need .zpost.eth
              </button>
            )}
            
            <button 
              className="btn btn-dark-red equal-btn" 
              style={{ marginLeft: '8px' }}
              onClick={() => {
                if (!isZwalletConnected) {
                  if (hasZwalletCreated) {
                    login({ mode: 'unlock', skipENSCheck: true });
                  } else {
                    showZwalletForCreation();
                  }
                } else {
                  setShowWalletModal(true);
                }
              }}
              disabled={authStatus === 'unlocking' || authStatus === 'creating-wallet'}
            >
              {authStatus === 'unlocking' ? 'Unlocking...' : 
               authStatus === 'creating-wallet' ? 'Creating...' : 'Wallet'}
            </button>
            
            <Link to="/mint">
              <button className="btn btn-dark-red equal-btn" style={{ marginLeft: '8px' }}>
                {ensName?.endsWith('.zpost.eth') ? ensName : '.zpost.eth'}
              </button>
            </Link>
            
            {isAuthenticated && ensName && (
              <Link to="/profile">
                <button className="btn btn-dark-red equal-btn" style={{ marginLeft: '8px' }}>
                  Profile
                </button>
              </Link>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default HeaderNav;
